import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import moment from 'moment';
import { Skeleton } from '@material-ui/lab';
import { addQueryWithReplace } from 'Helpers/Query';
import ScheduleDivider from './ScheduleDivider';
import ScheduleEvent from './ScheduleEvent';
import ScheduleSectionToggle from '../../ScheduleSectionToggle';

const START_HOUR = 7;
const END_HOUR = 18;

// isToday checks if the provided moment is the current day.
const isToday = date => date.isSame(moment(), 'day');

const overlaps = (e1, e2) => {
  const e1Start = moment(e1.startTime);
  const e1End = moment(e1.endTime);
  const e2Start = moment(e2.startTime);
  const e2End = moment(e2.endTime);

  return e1Start.isBefore(e2End) && e1End.isAfter(e2Start);
};

// m is a moment
const minuteOfDay = m => m.hour() * 60 + m.minute();

// maxOverlaps returns the maximum number of events that event overlaps with in
// the given list of events. This will be used to determine the width of the
// event when it gets rendered.
//
// Consider the following example:
//
// +----------------------+
// |          A           |
// +----------------------+
// +----------++----------+
// |    B     ||    C     |
// +----------++----------+
// +------++--------------+
// |      ||      E       |
// |      |+--------------+
// |  D   |+------++------+
// |      ||  F   ||  G   |
// |      |+------++------+
// |      |+--------------+
// +------+|              |
// +------+|      H       |
// |  I   ||              |
// +------++--------------+
//
// Event D overlaps with 4 other events (E, F, G, H), so you might assume that
// its width should be 20% (1 / (1 + 4) * 100). But the actual width should be
// 33% since it overlaps with at most 2 other events at any given time
// (1 / (2 + 1) * 100).
const maxOverlaps = (startTime, endTime, otherEvents) => {
  let mostOverlaps = 0;

  // From the start of event, to the end of event, in five minute intervals (arbitrary),
  // check how many overlaps there are and record the largest each time.
  // See the example above for why we need to do this.
  for (
    let i = minuteOfDay(moment(startTime));
    i <= minuteOfDay(moment(endTime));
    i += 5
  ) {
    let tmpOverlaps = 0;
    otherEvents.forEach((event) => {
      // a dummy event to represent the current time (same start and end)
      const curTime = {
        startTime: moment()
          .startOf('day')
          .minutes(i),
        endTime: moment()
          .startOf('day')
          .minutes(i),
      };
      if (overlaps(event, curTime)) {
        tmpOverlaps++;
      }
    });
    if (tmpOverlaps > mostOverlaps) {
      mostOverlaps = tmpOverlaps;
    }
  }

  return mostOverlaps;
};

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
  },
  subtitle: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.primary.main,
    margin: theme.spacing(2, 0),
    ...theme.typography.h4,
  },
  scheduleContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '367px',
    marginRight: theme.spacing(4),
  },
  calendarContainer: {
    display: 'flex',
    flex: 1,
    width: '367px',
    backgroundColor: theme.palette.common.white,
    borderRadius: '13px',
    flexDirection: 'column',
    position: 'relative',
  },
  calendarContainerLoading: {
    display: 'flex',
    flex: 1,
    width: '367px',
    borderRadius: '13px',
    flexDirection: 'column',
    position: 'relative',
  },
  eventContainer: {
    display: 'flex',
    flex: 1,
    position: 'relative',
    marginLeft: theme.spacing(8),
    marginRight: theme.spacing(3),
  },
});

const ScheduleDailyCalendar = ({
  className,
  classes,
  date,
  scheduleEvents,
  actionItemLinkCount,
  handleToggleDailyView,
  meetingCollectionLoading,
}) => {
  const [selectedEvent, setSelectedEvent] = useState();
  const now = moment();
  const offset30 = 1000 * 60 * 30; // / 12 * (hourEnd - hourStart); // 30 minute offset for alignment purposes
  const offset60 = 1000 * 60 * 60; // / 12 * (hourEnd - hourStart); // 60 minute offset for alignment purposes
  const hourStart = scheduleEvents.reduce(
    (acc, curr) => Math.min(acc, new Date(curr.attr('startTime')).getHours()),
    START_HOUR,
  );
  // TODO: We should handle calendar events that span multiple days. IE. What do we do if an end time is 8am tomorrow morning?
  const hourEnd = scheduleEvents.reduce(
    (acc, curr) => Math.max(
      acc,
      moment(curr.attr('endTime'))
        .add(1, 'hour')
        .hour(),
    ),
    END_HOUR,
  );

  // set the time bounds for the events we want to display
  const windowStart = moment(date)
    .startOf('day')
    .add(hourStart, 'h');

  const windowEnd = moment(date)
    .startOf('day')
    .add(hourEnd, 'h');

  const hoursToDisplay = [];
  for (let i = moment(windowStart); i <= windowEnd; i = i.clone().add(1, 'h')) {
    hoursToDisplay.push(i);
  }

  const displayNowLine = isToday(windowStart) && now.hour() >= hourStart && now.hour() <= hourEnd;

  const handleEventClick = (evt) => {
    setSelectedEvent(evt.id());
    addQueryWithReplace(
      { meetingId: evt.id() },
      { householdId: evt.attr('householdId') },
    );
  };

  return (
    <div className={classNames(classes.root, className)}>
      <div className={classes.scheduleContainer}>
        <div className={classes.subtitle}>
          {"Here's"} what your
          <ScheduleSectionToggle
            onClick={handleToggleDailyView}
            options={['day', 'week']}
            selected="day"
          />
          looks like:
        </div>
        {meetingCollectionLoading ? (
          <Skeleton
            animation="wave"
            variant="rect"
            width={367}
            className={classes.calendarContainerLoading}
          />
        ) : (
          <div className={classes.calendarContainer}>
            <div className={classes.eventContainer}>
              {hoursToDisplay.map(time => (
                <ScheduleDivider
                  time={time}
                  windowStart={windowStart}
                  windowEnd={windowEnd}
                  offset30={offset30}
                  offset60={offset60}
                />
              ))}
              {scheduleEvents.map((evt, i, arr) => {
                const startTime = evt.attr('startTime');
                const endTime = evt.attr('endTime');
                const title = evt.attr('title');

                return (
                  <ScheduleEvent
                    actionItemLinkCount={actionItemLinkCount[evt.id()]}
                    onClick={() => handleEventClick(evt)}
                    isSelected={evt.id() === selectedEvent}
                    title={title}
                    key={evt.id()}
                    startTime={startTime}
                    endTime={endTime}
                    windowStart={windowStart}
                    windowEnd={windowEnd}
                    offset30={offset30}
                    offset60={offset60}
                    numOverlaps={maxOverlaps(
                      startTime,
                      endTime,
                      arr.slice(0, i).concat(arr.slice(i + 1, arr.length)),
                    )}
                    numOverlapsBefore={maxOverlaps(
                      startTime,
                      endTime,
                      arr.slice(0, i),
                    )}
                  />
                );
              })}
              {displayNowLine && (
                <ScheduleDivider
                  time={now}
                  windowStart={windowStart}
                  windowEnd={windowEnd}
                  displayNowLine={displayNowLine}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

ScheduleDailyCalendar.defaultProps = {
  className: '',
};

ScheduleDailyCalendar.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  scheduleEvents: PropTypes.array.isRequired,
  date: PropTypes.object.isRequired,
  actionItemLinkCount: PropTypes.object.isRequired,
  handleToggleDailyView: PropTypes.func.isRequired,
  meetingCollectionLoading: PropTypes.bool.isRequired,
};

export default withStyles(styles)(ScheduleDailyCalendar);
